import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PlayerHeadshot from "../PlayerHeadshot";
import { getLastName } from "../../utils/textUtils";
import { colors } from "../../theme";
import { FIELD_SURFACE } from "../../utils/sportField";
import PlayerNameLink from "./PlayerNameLink";

const HEADSHOT_SIZES = {
  field: 64,
  battery: 88,
  favorite: 64,
  mini: 48,
};

const LineupSlot = ({
  slot,
  player,
  editable = false,
  size = "field",
  showPosition = true,
  pinToHeadshot = false,
  sport = "mlb",
  onSelect,
  onClear,
}) => {
  const headshotSize = HEADSHOT_SIZES[size] || HEADSHOT_SIZES.field;
  const filled = Boolean(player?.playerId);
  const clickable = editable || filled;
  const ringSize = headshotSize + 8;

  const handleClick = () => {
    if (editable) {
      onSelect?.(slot);
      return;
    }
    if (filled) onSelect?.(slot);
  };

  const positionSx = pinToHeadshot
    ? {
        position: "absolute",
        left: "50%",
        bottom: "100%",
        mb: 0.5,
        transform: "translateX(-50%)",
        whiteSpace: "nowrap",
      }
    : { lineHeight: 1 };

  const nameSx = pinToHeadshot
    ? {
        position: "absolute",
        left: "50%",
        top: "100%",
        mt: 0.5,
        transform: "translateX(-50%)",
        whiteSpace: "nowrap",
      }
    : { minHeight: 20, px: 0.5 };

  return (
    <Box
      onClick={clickable ? handleClick : undefined}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: pinToHeadshot ? 0 : 0.75,
        width: pinToHeadshot ? ringSize : headshotSize + 28,
        height: pinToHeadshot ? ringSize : "auto",
        cursor: clickable ? "pointer" : "default",
        "&:hover .slot-ring": {
          borderColor: editable ? "primary.main" : colors.border,
        },
      }}
    >
      {showPosition && (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.12em",
            color: "text.secondary",
            lineHeight: 1,
            ...positionSx,
          }}
        >
          {slot.short}
        </Typography>
      )}
      <Box
        className="slot-ring"
        sx={{
          width: ringSize,
          height: ringSize,
          borderRadius: "50%",
          border: filled
            ? `2px solid ${colors.positive}`
            : `2px dashed ${colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: filled
            ? "rgba(34, 197, 94, 0.08)"
            : pinToHeadshot
              ? FIELD_SURFACE[sport] || FIELD_SURFACE.mlb
              : "rgba(255,255,255,0.03)",
          transition: "border-color 0.15s ease",
        }}
      >
        {filled ? (
          <PlayerHeadshot
            playerId={player.playerId}
            src={player.photoUrl}
            sport={sport}
            size={headshotSize}
            showTooltip={false}
            showLoading={false}
          />
        ) : (
          <AddIcon
            sx={{
              color: editable ? "text.secondary" : "transparent",
              fontSize: headshotSize * 0.4,
            }}
          />
        )}
      </Box>
      {filled ? (
        <Box sx={{ textAlign: "center", ...nameSx }}>
          <PlayerNameLink
            playerId={player.playerId}
            name={getLastName(player.name)}
            variant="body2"
            fontWeight={600}
            showIcon={!pinToHeadshot}
            sport={sport}
            linkable={player.linkable !== false}
          />
        </Box>
      ) : (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            textAlign: "center",
            lineHeight: 1.2,
            ...nameSx,
          }}
        >
          {editable ? "Pick" : "Open"}
        </Typography>
      )}
      {editable && filled && (
        <IconButton
          size="small"
          aria-label={`Clear ${slot.label}`}
          onClick={(event) => {
            event.stopPropagation();
            onClear?.(slot);
          }}
          sx={{
            position: "absolute",
            top: pinToHeadshot ? -4 : 14,
            right: pinToHeadshot ? -4 : 0,
            width: 22,
            height: 22,
            bgcolor: "background.paper",
            border: `1px solid ${colors.border}`,
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default LineupSlot;
